// Q3/QFusion `.shader` script parsing (fetched from the map-server's
// /shader and /shaders endpoints). Drives the skybox plus animated / emissive /
// transparent / scrolled / rotated / color-cycled / fogged materials.

import { api } from "../base";

/**
 * GL cull mode. Q3/QFusion BSP surfaces are wound so the visible side is a GL
 * *back* face (`glFrontFace` is CCW here), which is why the default is
 * `front` (cull front faces, keep the visible back ones) — matching QFusion's
 * `SHADER_CULL_FRONT` default.
 */
export type CullMode = "back" | "front" | "none";

/** Q3 `wavefunc` names. */
export type WaveFunc = "sin" | "triangle" | "square" | "sawtooth" | "inversesawtooth" | "noise";

/** A texture-coordinate modifier (`tcMod`). */
export interface TcMod {
  kind: "scroll" | "rotate" | "scale" | "stretch";
  /** scroll: units/sec. */
  s?: number;
  t?: number;
  /** rotate: degrees/sec. */
  rotateSpeed?: number;
  /** scale: multipliers. */
  sx?: number;
  sy?: number;
  /** stretch: wave parameters. */
  func?: WaveFunc;
  base?: number;
  amp?: number;
  phase?: number;
  freq?: number;
}

/**
 * A color/alpha generator (`rgbGen`/`alphaGen`). `wave` uses `func/base/amp/
 * phase/freq`; `const` uses `r/g/b` (alpha uses `r`); `vertex`/`exactvertex`
 * use the interpolated vertex color; `portal` is a distance fade.
 */
export interface ColorGen {
  kind:
    | "identity"
    | "const"
    | "wave"
    | "vertex"
    | "exactvertex"
    | "lightingdiffuse"
    | "lightingspecular"
    | "entity"
    | "portal";
  r?: number;
  g?: number;
  b?: number;
  func?: WaveFunc;
  base?: number;
  amp?: number;
  phase?: number;
  freq?: number;
}

export interface ShaderStage {
  /** Frame texture paths (one for `map`, several for `animmap`). */
  maps: string[];
  /** `clampmap` uses clamp-to-edge wrapping. */
  clamp?: boolean;
  animFreq: number;
  /** Raw `blendfunc` string (e.g. "add", "blend", "GL_DST_COLOR GL_ZERO"). */
  blend?: string;
  rgb?: ColorGen;
  alpha?: ColorGen;
  /** Q3 `alphaFunc`: alpha-test/discard instead of blending (grates, foliage). */
  alphaFunc?: "gt0" | "lt128" | "ge128";
  /** Q3 `depthFunc equal`: only draw where an earlier pass wrote depth. */
  depthFuncEqual?: boolean;
  tcMods: TcMod[];
  tcGen?: "environment" | "lightmap" | "vector" | "ivector";
  /** `tcGen vector`: the two 3-vectors the world position is dotted with. */
  tcGenS?: [number, number, number];
  tcGenT?: [number, number, number];
  deform?: Deform;
}

/** Q3 `fogparms ( r g b ) dist`. */
export interface FogParms {
  r: number;
  g: number;
  b: number;
  dist: number;
}

export interface ShaderDef {
  name: string;
  surfaceParms: Set<string>;
  skyparmsBase?: string;
  stages: ShaderStage[];
  fog?: FogParms;
  /** Shader has the top-level `portal` keyword (render a portal view). */
  portal?: boolean;
  /** `cull` directive (Q3/QFusion: back/backside/backsided, front, disable/none). */
  cull?: CullMode;
  /** `polygonoffset` directive: bias the surface toward the camera (decals). */
  polygonOffset?: boolean;
  /**
   * Compiler-generated cel-outline shell (`q3map_invert` + `q3map_offset`).
   * These are baked duplicate surfaces that only look right with Q3's stage
   * depth rules; drawn as normal geometry they cover the world with black.
   * The client skips them (cosmetic outlines only).
   */
  outlineShell?: boolean;
}

// Positive results only. A shader that resolves to `null` may do so only
// because of the *currently loaded* map's pk3 (the server resolves shader
// scripts per map), so caching negatives across map loads would leave surfaces
// untextured after a map switch. `clearShaderCache()` also runs on map load.
const cache = new Map<string, ShaderDef>();

/** Drop cached shader defs (called when a new map is loaded). */
export function clearShaderCache(): void {
  cache.clear();
}

// The map whose pk3 should be searched first; set on each map load so shader
// requests are map-qualified (deterministic resolution + cache-busting).
let shaderMap = "";

export function setShaderMap(name: string): void {
  shaderMap = name || "";
}

function mapQuery(): string {
  return shaderMap ? `&map=${encodeURIComponent(shaderMap)}` : "";
}

/** Fetch + parse a shader script block by name (cached; positives only). */
export async function loadShader(name: string): Promise<ShaderDef | null> {
  const key = name.toLowerCase();
  if (cache.has(key)) return cache.get(key)!;
  let def: ShaderDef | null = null;
  try {
    const res = await fetch(api(`/shader?name=${encodeURIComponent(name)}${mapQuery()}`));
    if (res.ok) {
      def = parseShader(`${name} ${await res.text()}`, name);
    }
  } catch {
    def = null;
  }
  if (def) cache.set(key, def);
  return def;
}

/**
 * Bulk-fetch + parse shader defs for many names (batched), caching the results.
 * Returns a map of name -> def (absent/falsey if not found).
 *
 * Requests are split by encoded URL length. A single request for a shader-heavy
 * map (simpsons_q3: 434 unique shaders → ~16.3 KB of names) blows Node's 16 KB
 * request-header limit: Caddy/Node answer **431 Request Header Fields Too
 * Large**, and the old code then silently set *every* def to null — the whole
 * map rendered with fallback materials (no alphaFunc / blendfunc /
 * polygonoffset / tcMod), e.g. the coplanar road markings became opaque black
 * quads that z-fought the road.
 */
export async function loadShaders(names: string[]): Promise<Map<string, ShaderDef | null>> {
  const out = new Map<string, ShaderDef | null>();
  const missing: string[] = [];
  for (const n of names) {
    const key = n.toLowerCase();
    if (cache.has(key)) out.set(key, cache.get(key)!);
    else missing.push(n);
  }
  // ~4 KB of names per request leaves room for the rest of the headers.
  const CHUNK = 4000;
  const chunks: string[][] = [];
  let cur: string[] = [];
  let curLen = 0;
  for (const n of missing) {
    const add = encodeURIComponent(n).length + 3; // comma + slack
    if (cur.length && curLen + add > CHUNK) {
      chunks.push(cur);
      cur = [];
      curLen = 0;
    }
    cur.push(n);
    curLen += add;
  }
  if (cur.length) chunks.push(cur);

  for (const chunk of chunks) {
    let ok = false;
    try {
      const res = await fetch(api(`/shaders?names=${encodeURIComponent(chunk.join(","))}${mapQuery()}`));
      if (res.ok) {
        const json = (await res.json()) as Record<string, string>;
        for (const n of chunk) {
          const block = json[n];
          const def = block ? parseShader(`${n} ${block}`, n) : null;
          // Only cache positives (negatives may differ per loaded map).
          if (def) cache.set(n.toLowerCase(), def);
          out.set(n.toLowerCase(), def);
        }
        ok = true;
      }
    } catch {
      /* fall through to the per-name retry */
    }
    if (!ok) {
      // A failed batch must not blank the map: fetch these one by one (short
      // URLs) so the materials still get their real shader scripts.
      console.warn(`shader batch failed (${chunk.length} names); retrying individually`);
      await Promise.all(
        chunk.map(async (n) => {
          if (!out.has(n.toLowerCase())) out.set(n.toLowerCase(), await loadShader(n));
        }),
      );
    }
  }
  return out;
}

/** Tokenize, splitting on `{}()`, and stripping C-style `//` comments. */
function tokenize(source: string): string[] {
  const s = source.replace(/\/\/[^\n]*/g, " ");
  const tokens: string[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (c === "{" || c === "}" || c === "(" || c === ")") {
      tokens.push(c);
      i++;
      continue;
    }
    let j = i;
    while (j < s.length && !/[\s{}()]/.test(s[j])) j++;
    tokens.push(s.slice(i, j).replace(/^"|"$/g, ""));
    i = j;
  }
  return tokens;
}

function parseWaveFunc(t: string | undefined): WaveFunc {
  switch ((t ?? "").toLowerCase()) {
    case "triangle":
      return "triangle";
    case "square":
      return "square";
    case "sawtooth":
      return "sawtooth";
    case "inversesawtooth":
      return "inversesawtooth";
    case "noise":
      return "noise";
    default:
      return "sin";
  }
}

function num(t: string | undefined, fallback = 0): number {
  const v = parseFloat(t ?? "");
  return Number.isFinite(v) ? v : fallback;
}

/** Skip an optional `(` and return the new index. */
function skipOpen(tokens: string[], i: number): number {
  return tokens[i] === "(" ? i + 1 : i;
}
function skipClose(tokens: string[], i: number): number {
  return tokens[i] === ")" ? i + 1 : i;
}

/** Strip a trailing image extension from a shader `map` name (Q3 strips these). */
function stripExt(name: string): string {
  if (name.startsWith("$")) return name;
  return name.replace(/\.(tga|jpg|jpeg|png|webp|dds|pcx)$/i, "");
}

/** Parse a `cull` argument (QFusion accepts back/backside/backsided, front, disable/none/twosided). */
function parseCull(t: string | undefined): CullMode {
  switch ((t ?? "").toLowerCase()) {
    case "disable":
    case "none":
    case "twosided":
      return "none";
    case "back":
    case "backside":
    case "backsided":
      return "back";
    default:
      return "front";
  }
}

export function parseShader(source: string, fallbackName: string): ShaderDef {
  const tokens = tokenize(source);
  const surfaceParms = new Set<string>();
  const stages: ShaderStage[] = [];
  let skyparmsBase: string | undefined;
  let fog: FogParms | undefined;
  let portal = false;
  let shaderDeform: Deform | undefined;
  let q3mapInvert = false;
  let q3mapOffset = false;
  let polygonOffset = false;
  let cull: CullMode | undefined;
  let name = fallbackName;

  let i = 0;
  if (tokens[i] && tokens[i] !== "{") {
    name = tokens[i];
    i++;
  }
  if (tokens[i] === "{") i++;

  while (i < tokens.length) {
    const raw = tokens[i];
    const t = raw.toLowerCase();
    if (raw === "}") {
      i++;
      break;
    }
    if (t === "surfaceparm") {
      if (tokens[i + 1]) surfaceParms.add(tokens[i + 1].toLowerCase());
      i += 2;
    } else if (t === "skyparms") {
      // `skyparms <box> <cloudheight> <skybox>`: a `-` (or `0`) box means the
      // map has NO custom skybox — the sky is a flat colour (DM6: `- 512 -`).
      // Treating `-` as a real base name made us load `-_rt`… (all 404) and
      // draw an untextured grey skybox: a flat grey sheet floating in the map.
      const box = tokens[i + 1];
      skyparmsBase = box && box !== "-" && box !== "0" ? box : undefined;
      i += 4;
    } else if (t === "fogparms") {
      i = skipOpen(tokens, i + 1);
      const r = num(tokens[i]);
      const g = num(tokens[i + 1]);
      const b = num(tokens[i + 2]);
      i = skipClose(tokens, i + 3);
      const dist = num(tokens[i]);
      fog = { r, g, b, dist: dist > 0 ? dist : 300 };
      i += 1;
    } else if (t === "portal") {
      portal = true;
      i += 1;
    } else if (t === "q3map_invert") {
      q3mapInvert = true;
      i += 1;
    } else if (t === "q3map_offset") {
      q3mapOffset = true;
      i += 2;
    } else if (t === "polygonoffset") {
      polygonOffset = true;
      i += 1;
    } else if (t === "cull") {
      cull = parseCull(tokens[i + 1]);
      i += 2;
    } else if (t === "deformvertexes" || t === "deform") {
      // Shader-level deformVertexes applies to all stages.
      const [d, ni] = parseDeform(tokens, i + 1);
      if (d) shaderDeform = d;
      i = ni;
    } else if (raw === "{") {
      const stage: ShaderStage = { maps: [], animFreq: 0, tcMods: [] };
      i++;
      while (i < tokens.length && tokens[i] !== "}") {
        const k = tokens[i].toLowerCase();
        if (k === "map" || k === "clampmap") {
          if (tokens[i + 1]) stage.maps.push(stripExt(tokens[i + 1]));
          if (k === "clampmap") stage.clamp = true;
          i += 2;
        } else if (k === "animmap") {
          stage.animFreq = num(tokens[i + 1]);
          i += 2;
          while (i < tokens.length && tokens[i] !== "}" && !isStageKeyword(tokens[i])) {
            stage.maps.push(stripExt(tokens[i]));
            i++;
          }
        } else if (k === "blendfunc") {
          const a = tokens[i + 1];
          const b = tokens[i + 2];
          if (a && b && isGlConst(b)) {
            stage.blend = `${a} ${b}`.toLowerCase();
            i += 3;
          } else {
            stage.blend = a?.toLowerCase();
            i += 2;
          }
        } else if (k === "rgbgen") {
          i = parseColorGen(tokens, i + 1, false, (cg) => (stage.rgb = cg));
        } else if (k === "alphagen") {
          i = parseColorGen(tokens, i + 1, true, (cg) => (stage.alpha = cg));
        } else if (k === "alphafunc") {
          const v = (tokens[i + 1] ?? "").toLowerCase();
          if (v === "gt0") stage.alphaFunc = "gt0";
          else if (v === "lt128") stage.alphaFunc = "lt128";
          else stage.alphaFunc = "ge128";
          i += 2;
        } else if (k === "depthfunc") {
          if ((tokens[i + 1] ?? "").toLowerCase() === "equal") stage.depthFuncEqual = true;
          i += 2;
        } else if (k === "tcmod") {
          i = parseTcMod(tokens, i + 1, stage);
        } else if (k === "tcgen") {
          const v = (tokens[i + 1] ?? "").toLowerCase();
          if (v === "vector" || v === "ivector") {
            // tcGen vector (sx sy sz) (tx ty tz): UV from dot(worldPos, vec).
            stage.tcGen = v;
            stage.tcGenS = [num(tokens[i + 2]), num(tokens[i + 3]), num(tokens[i + 4])];
            stage.tcGenT = [num(tokens[i + 5]), num(tokens[i + 6]), num(tokens[i + 7])];
            i += 8;
          } else if (v === "environment" || v === "lightmap") {
            stage.tcGen = v;
            i += 2;
          } else {
            i += 2;
          }
        } else if (k === "deformvertexes" || k === "deform") {
          const [d, ni] = parseDeform(tokens, i + 1);
          if (d) stage.deform = d;
          i = ni;
        } else {
          i++;
        }
      }
      i++; // closing }
      if (stage.maps.length) stages.push(stage);
    } else {
      i++;
    }
  }

  // `surfaceparm fog` implies a fog volume; give it a default if `fogparms`
  // was omitted.
  if (!fog && surfaceParms.has("fog")) fog = { r: 0.5, g: 0.5, b: 0.5, dist: 300 };

  // A shader-level `deformVertexes` applies to any stage that lacks its own.
  if (shaderDeform) {
    for (const s of stages) {
      if (!s.deform) s.deform = shaderDeform;
    }
  }

  return {
    name,
    surfaceParms,
    skyparmsBase,
    stages,
    fog,
    portal,
    cull,
    polygonOffset,
    outlineShell: q3mapInvert && q3mapOffset,
  };
}

/** Parse the value after `rgbgen`/`alphagen`; returns the next index. */
function parseColorGen(tokens: string[], i: number, isAlpha: boolean, set: (cg: ColorGen) => void): number {
  const kind = (tokens[i] ?? "").toLowerCase();
  switch (kind) {
    case "const": {
      let j = skipOpen(tokens, i + 1);
      if (isAlpha) {
        const r = num(tokens[j]);
        j = skipClose(tokens, j + 1);
        set({ kind: "const", r });
      } else {
        const r = num(tokens[j]);
        const g = num(tokens[j + 1]);
        const b = num(tokens[j + 2]);
        j = skipClose(tokens, j + 3);
        set({ kind: "const", r, g, b });
      }
      return j;
    }
    case "wave": {
      const func = parseWaveFunc(tokens[i + 1]);
      set({
        kind: "wave",
        func,
        base: num(tokens[i + 2]),
        amp: num(tokens[i + 3]),
        phase: num(tokens[i + 4]),
        freq: num(tokens[i + 5]),
      });
      return i + 6;
    }
    case "vertex":
      set({ kind: "vertex" });
      return i + 1;
    case "exactvertex":
      set({ kind: "exactvertex" });
      return i + 1;
    case "lightingdiffuse":
      set({ kind: "lightingdiffuse" });
      return i + 1;
    case "lightingspecular":
      set({ kind: "lightingspecular" });
      return i + 1;
    case "entity":
      set({ kind: "entity" });
      return i + 1;
    case "portal":
      set({ kind: "portal" });
      return i + 1;
    case "identity":
      set({ kind: "identity" });
      return i + 1;
    default:
      // Unknown/omitted: look for a bare `$whiteimage`-style no-op; skip one.
      set({ kind: "identity" });
      return i + 1;
  }
}

/** Parse the value after `tcmod`; returns the next index. */
function parseTcMod(tokens: string[], i: number, stage: ShaderStage): number {
  const kind = (tokens[i] ?? "").toLowerCase();
  switch (kind) {
    case "scroll":
      stage.tcMods.push({ kind: "scroll", s: num(tokens[i + 1]), t: num(tokens[i + 2]) });
      return i + 3;
    case "rotate":
      stage.tcMods.push({ kind: "rotate", rotateSpeed: num(tokens[i + 1]) });
      return i + 2;
    case "scale":
      stage.tcMods.push({ kind: "scale", sx: num(tokens[i + 1], 1), sy: num(tokens[i + 2], 1) });
      return i + 3;
    case "stretch":
      stage.tcMods.push({
        kind: "stretch",
        func: parseWaveFunc(tokens[i + 1]),
        base: num(tokens[i + 2], 1),
        amp: num(tokens[i + 3]),
        phase: num(tokens[i + 4]),
        freq: num(tokens[i + 5]),
      });
      return i + 6;
    default:
      return i + 1;
  }
}

/** Parse the value after `deformVertexes`; returns the deform + next index. */
function parseDeform(tokens: string[], i: number): [Deform | null, number] {
  const kind = (tokens[i] ?? "").toLowerCase();
  switch (kind) {
    case "wave":
      return [
        {
          kind: "wave",
          div: num(tokens[i + 1], 1),
          func: parseWaveFunc(tokens[i + 2]),
          base: num(tokens[i + 3]),
          amp: num(tokens[i + 4]),
          phase: num(tokens[i + 5]),
          freq: num(tokens[i + 6]),
        },
        i + 7,
      ];
    case "move":
      return [
        {
          kind: "move",
          move: [num(tokens[i + 1]), num(tokens[i + 2]), num(tokens[i + 3])],
          func: parseWaveFunc(tokens[i + 4]),
          base: num(tokens[i + 5]),
          amp: num(tokens[i + 6]),
          phase: num(tokens[i + 7]),
          freq: num(tokens[i + 8]),
        },
        i + 9,
      ];
    case "normal":
      return [{ kind: "normal", amp: num(tokens[i + 1]), freq: num(tokens[i + 2]) }, i + 3];
    case "bulge":
      return [
        {
          kind: "bulge",
          bulgeWidth: num(tokens[i + 1], 1),
          bulgeHeight: num(tokens[i + 2]),
          bulgeSpeed: num(tokens[i + 3]),
        },
        i + 4,
      ];
    case "autosprite":
      return [{ kind: "autosprite" }, i + 1];
    case "autosprite2":
      return [{ kind: "autosprite2" }, i + 1];
    case "projectionshadow":
      return [null, i + 1];
    default:
      return [null, i + 1];
  }
}

function isStageKeyword(t: string): boolean {
  return [
    "map",
    "clampmap",
    "animmap",
    "blendfunc",
    "rgbgen",
    "alphagen",
    "tcmod",
    "tcgen",
    "deformvertexes",
    "deform",
    "depthfunc",
    "depthwrite",
    "detail",
    "alphafunc",
  ].includes(t.toLowerCase());
}

function isGlConst(t: string): boolean {
  return /^gl_/i.test(t);
}

/** Q3/OpenGL blend factors used by `blendfunc`. */
export type BlendFactor =
  | "zero"
  | "one"
  | "srcColor"
  | "oneMinusSrcColor"
  | "srcAlpha"
  | "oneMinusSrcAlpha"
  | "dstColor"
  | "oneMinusDstColor"
  | "dstAlpha"
  | "oneMinusDstAlpha";

/** Explicit `blendfunc <src> <dst>` pair (Q3 uses many combinations). */
export interface BlendPair {
  src: BlendFactor;
  dst: BlendFactor;
}

/**
 * A material blend. The named modes keep the common Q3 combinations readable
 * (and let the renderer decide depth-write behaviour), while `BlendPair` covers
 * the rest — e.g. water's `blendFunc GL_dst_color GL_one`, which must not
 * silently fall back to opaque.
 */
export type BlendMode = "opaque" | "add" | "addAlpha" | "blend" | "filter" | "blacken" | BlendPair;

const BLEND_FACTORS: Record<string, BlendFactor> = {
  gl_zero: "zero",
  gl_one: "one",
  gl_src_color: "srcColor",
  gl_one_minus_src_color: "oneMinusSrcColor",
  gl_src_alpha: "srcAlpha",
  gl_one_minus_src_alpha: "oneMinusSrcAlpha",
  gl_dst_color: "dstColor",
  gl_one_minus_dst_color: "oneMinusDstColor",
  gl_dst_alpha: "dstAlpha",
  gl_one_minus_dst_alpha: "oneMinusDstAlpha",
};

function blendMode(blend?: string): BlendMode {
  if (!blend) return "opaque";
  const s = blend.toLowerCase().trim();
  if (s === "add") return "add";
  if (s === "filter") return "filter";
  if (s === "blend") return "blend";
  const parts = s.split(/\s+/);
  if (parts.length === 2) {
    const src = BLEND_FACTORS[parts[0]];
    const dst = BLEND_FACTORS[parts[1]];
    if (src && dst) {
      // Canonical Q3 combinations (also keeps depth-write decisions explicit).
      if (src === "one" && dst === "zero") return "opaque";
      if (src === "one" && dst === "one") return "add";
      if (src === "srcAlpha" && dst === "one") return "addAlpha";
      if (src === "srcAlpha" && dst === "oneMinusSrcAlpha") return "blend";
      if (src === "dstColor" && dst === "zero") return "filter";
      if (src === "zero" && dst === "oneMinusSrcColor") return "blacken";
      return { src, dst };
    }
  }
  return "opaque";
}

/** A `deformVertexes` vertex displacement. */
export interface Deform {
  kind: "wave" | "move" | "normal" | "bulge" | "autosprite" | "autosprite2";
  /** wave: spatial divisor. */
  div?: number;
  /** move: displacement axis/magnitude. */
  move?: [number, number, number];
  func?: WaveFunc;
  base?: number;
  amp?: number;
  phase?: number;
  freq?: number;
  /** bulge: <width> <height> <speed>. */
  bulgeWidth?: number;
  bulgeHeight?: number;
  bulgeSpeed?: number;
}

/** Per-stage render effects carried to the renderer. */
export interface StageEffects {
  tcMods: TcMod[];
  rgb?: ColorGen;
  alpha?: ColorGen;
  /** Q3 `alphaFunc`: discard fragments by alpha (grates, foliage, banners). */
  alphaFunc?: "gt0" | "lt128" | "ge128";
  /** Q3 `depthFunc equal`: only draw where an earlier pass wrote depth. */
  depthFuncEqual?: boolean;
  /** Texture-coordinate generation (overrides the affine tcMod when set). */
  tcGen?: "environment" | "lightmap" | "vector" | "ivector";
  /** `tcGen vector`: the two 3-vectors the world position is dotted with. */
  tcGenS?: [number, number, number];
  tcGenT?: [number, number, number];
  deform?: Deform;
}

export interface StagePlan {
  tex?: string;
  anim?: string[];
  animFreq: number;
  blend: BlendMode;
  clamp: boolean;
  effects: StageEffects;
}

/**
 * A normalized render plan derived from a shader script: a base diffuse pass
 * (optionally lit by the lightmap, animated, tcModded, color/alpha-generated,
 * blended) plus overlay passes, in order, and optional whole-shader fog.
 */
export interface ShaderPlan {
  lit: boolean;
  base: StagePlan;
  overlays: StagePlan[];
  fog?: FogParms;
  noDraw: boolean;
  /** Render a portal view (render-to-texture mirror) for this surface. */
  portal: boolean;
  /** Effective face-culling mode (`cull` directive, default back). */
  cull: CullMode;
  /** Bias this surface toward the camera to keep coplanar decals stable. */
  polygonOffset: boolean;
  /** Q3 fog volume (`surfaceparm fog`): draw as coloured fog, not a texture. */
  fogVolume: boolean;
}

function toStagePlan(s: ShaderStage): StagePlan {
  const anim = s.maps.length > 1 ? s.maps : undefined;
  return {
    tex: s.maps[0],
    anim,
    animFreq: s.animFreq,
    blend: blendMode(s.blend),
    clamp: !!s.clamp,
    effects: {
      tcMods: s.tcMods,
      rgb: s.rgb,
      alpha: s.alpha,
      alphaFunc: s.alphaFunc,
      depthFuncEqual: s.depthFuncEqual,
      tcGen: s.tcGen,
      tcGenS: s.tcGenS,
      tcGenT: s.tcGenT,
      deform: s.deform,
    },
  };
}

/** Translate a `ShaderDef` into a `ShaderPlan`. */
export function buildPlan(def: ShaderDef): ShaderPlan {
  const noDraw =
    def.surfaceParms.has("nodraw") ||
    def.surfaceParms.has("sky") ||
    def.surfaceParms.has("trigger") ||
    def.surfaceParms.has("playerclip") ||
    def.surfaceParms.has("clip");

  // Which stages carry a real texture vs `$lightmap`.
  const hasLightmapStage = def.stages.some((s) => s.maps.some((m) => m === "$lightmap"));
  const textureStages = def.stages.filter((s) => s.maps.some((m) => m !== "$lightmap" && m !== "$whiteimage"));

  const nolightmap = def.surfaceParms.has("nolightmap");
  const baseStage = textureStages[0];
  const baseBlend = baseStage ? blendMode(baseStage.blend) : "opaque";
  const lit = !nolightmap && (hasLightmapStage || def.stages.length === 0 || baseBlend === "opaque");

  const base: StagePlan = baseStage
    ? toStagePlan(baseStage)
    : { animFreq: 0, blend: "opaque", clamp: false, effects: { tcMods: [] } };
  // A "filter" base multiplies against the cleared background; treat as opaque
  // for real surfaces. Decal shaders (`surfaceparm nonsolid` over a lightmap)
  // sit on top of an already-lit surface: keep the multiply blend and don't
  // write depth, otherwise the coplanar overlay z-fights/flickers.
  if (base.blend === "filter" && !(def.surfaceParms.has("nonsolid") && hasLightmapStage)) {
    base.blend = "opaque";
  }
  // Q3 liquids are translucent even without an explicit `blendFunc` — maps that
  // rely on `surfaceparm water/slime` alone otherwise render them opaque.
  // (`lava` and `trans` do **not** imply blending in Q3: many lava surfaces have
  // no blendfunc and are meant to be opaque. Forcing a blend made them dark /
  // see-through over the void.)
  if (
    base.blend === "opaque" &&
    (def.surfaceParms.has("water") || def.surfaceParms.has("slime"))
  ) {
    base.blend = "blend";
  }

  const overlays = textureStages.slice(1).map((s) => toStagePlan(s));

  return {
    lit,
    base,
    overlays,
    fog: def.fog,
    noDraw,
    portal: !!def.portal,
    cull: def.cull ?? "front",
    polygonOffset:
      !!def.polygonOffset || (def.surfaceParms.has("nonsolid") && base.blend === "filter"),
    fogVolume: def.surfaceParms.has("fog"),
  };
}

/** Evaluate a Q3 wave function at `t` seconds for `ColorGen`/`TcMod`. */
export function evalWave(func: WaveFunc | undefined, base: number, amp: number, phase: number, freq: number, t: number): number {
  const x = phase + t * freq;
  switch (func) {
    case "triangle": {
      const f = x - Math.floor(x);
      return base + amp * (f < 0.5 ? f * 2 : 2 - f * 2);
    }
    case "square":
      return base + amp * ((x - Math.floor(x)) < 0.5 ? 1 : 0);
    case "sawtooth":
      return base + amp * (x - Math.floor(x));
    case "inversesawtooth":
      return base + amp * (1 - (x - Math.floor(x)));
    case "noise":
      // Cheap deterministic hash noise (no Math.random, stays reproducible).
      return base + amp * (0.5 + 0.5 * Math.sin(x * 12.9898) * Math.cos(x * 78.233));
    default:
      return base + amp * Math.sin(x * Math.PI * 2);
  }
}

/**
 * Numeric id for a wave function, for the shader (0=sin,1=triangle,2=square,
 * 3=sawtooth,4=inversesawtooth,5=noise).
 */
export function waveFuncId(f: WaveFunc | undefined): number {
  switch (f) {
    case "triangle":
      return 1;
    case "square":
      return 2;
    case "sawtooth":
      return 3;
    case "inversesawtooth":
      return 4;
    case "noise":
      return 5;
    default:
      return 0;
  }
}

/**
 * Compose a stage's `tcMod`s into a single UV affine transform at `time`.
 * Returns `[m00, m10, m01, m11, tx, ty]` for the shader's
 * `uv = v_uv.x * u_uv_x + v_uv.y * u_uv_y + u_uv_off` mapping.
 */
export function computeUv(tcMods: TcMod[], time: number): [number, number, number, number, number, number] {
  let a = 1, b = 0, c = 0, d = 1, tx = 0, ty = 0;
  for (const m of tcMods) {
    let na = 1, nb = 0, nc = 0, nd = 1, ntx = 0, nty = 0;
    if (m.kind === "scroll") {
      ntx = (m.s ?? 0) * time;
      nty = (m.t ?? 0) * time;
    } else if (m.kind === "scale") {
      na = m.sx ?? 1;
      nd = m.sy ?? 1;
    } else if (m.kind === "stretch") {
      const s = evalWave(m.func, m.base ?? 1, m.amp ?? 0, m.phase ?? 0, m.freq ?? 0, time);
      na = s;
      nd = s;
    } else if (m.kind === "rotate") {
      const ang = (((m.rotateSpeed ?? 0) * Math.PI) / 180) * time;
      const cs = Math.cos(ang);
      const sn = Math.sin(ang);
      na = cs; nb = -sn; nc = sn; nd = cs;
      const cx = 0.5, cy = 0.5; // rotate about the texture centre
      ntx = cx - (cs * cx - sn * cy);
      nty = cy - (sn * cx + cs * cy);
    }
    // Compose: new = N ∘ current (N·M, N·o + t).
    const a2 = na * a + nb * c;
    const b2 = na * b + nb * d;
    const c2 = nc * a + nd * c;
    const d2 = nc * b + nd * d;
    const tx2 = na * tx + nb * ty + ntx;
    const ty2 = nc * tx + nd * ty + nty;
    a = a2; b = b2; c = c2; d = d2; tx = tx2; ty = ty2;
  }
  return [a, c, b, d, tx, ty];
}

export interface RgbResult {
  r: number;
  g: number;
  b: number;
  vertex: boolean;
  portal: boolean;
}

export function computeRgb(g: ColorGen | undefined, time: number): RgbResult {
  if (!g || g.kind === "identity") return { r: 1, g: 1, b: 1, vertex: false, portal: false };
  switch (g.kind) {
    case "const":
      return { r: g.r ?? 1, g: g.g ?? 1, b: g.b ?? 1, vertex: false, portal: false };
    case "wave": {
      const s = evalWave(g.func, g.base ?? 1, g.amp ?? 0, g.phase ?? 0, g.freq ?? 0, time);
      return { r: s, g: s, b: s, vertex: false, portal: false };
    }
    case "vertex":
    case "exactvertex":
      return { r: 1, g: 1, b: 1, vertex: true, portal: false };
    case "portal":
      return { r: 1, g: 1, b: 1, vertex: false, portal: true };
    default:
      return { r: 1, g: 1, b: 1, vertex: false, portal: false };
  }
}

export function computeAlpha(a: ColorGen | undefined, time: number): { value: number; portal: boolean } {
  if (!a || a.kind === "identity") return { value: 1, portal: false };
  switch (a.kind) {
    case "const":
      return { value: a.r ?? 1, portal: false };
    case "wave":
      return { value: evalWave(a.func, a.base ?? 1, a.amp ?? 0, a.phase ?? 0, a.freq ?? 0, time), portal: false };
    case "portal":
      return { value: 1, portal: true };
    default:
      return { value: 1, portal: false };
  }
}
